import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ContentEditor from './ContentEditor';
import AdminSettings from './AdminSettings';
import SimpleFormationsAdmin from './SimpleFormationsAdmin';
import ConnectionStatus from './ConnectionStatus';
import apiClient from '../../services/apiClient';
import './AdminDashboard.css';

const AdminDashboard = ({ onLogout }) => {
  const { content, saveContent, isLoading, hasUnsavedChanges } = useContent();
  const [selectedSection, setSelectedSection] = useState('home.hero');
  const [saveMessage, setSaveMessage] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Define the content structure for the sidebar
  const contentStructure = {
    'Page d\'accueil': {
      'home.hero': 'Section héros',
      'home.featuredPrograms': 'Programmes phares',
      'home.featuresHighlight': 'Fonctionnalités en vedette',
      'home.expertTestimonials': 'Témoignages de nos Experts',
      'home.participantTestimonials': 'Témoignages de nos Participants',
      'home.upcomingSessions': 'Sessions à venir',
      'home.expertGallery': 'Galerie Photo/Vidéo',
      'home.cta': 'Appel à l\'action'
    },
    'Page À propos': {
      'about.pageHeader': 'En-tête de page',
      'about.coreValues': 'Valeurs fondamentales',
      'about.threeBlocks': '3 Blocs: Histoire, Vision & Mission',
      'about.chronologie': 'Chronologie - Historique détaillé',
      'about.cta': 'Appel à l\'action'
    },
    'Page Services': {
      'services.pageHeader': 'En-tête de page',
      'services.mainServices': 'Services Principaux - 4 Secteurs',
      'services.cta': 'Appel à l\'action'
    },
    'Page Formations': {
      'formations.pageHeader': 'En-tête de page',
      'formations.formationsGrid': 'Grille des 9 Formations',
      'formations.calendar': 'Calendrier Global des Sessions',
      'formations.registrationForm': 'Formulaire d\'inscription',
      'formations.cta': 'Appel à l\'action'
    },
    'Page Experts': {
      'experts.pageHeader': 'En-tête de page',
      'experts.team': 'Équipe de Formateurs Experts'
    },
    'Page Contact': {
      'contact.pageHeader': 'En-tête de page',
      'contact.contactInfo': 'Informations de contact',
      'contact.contactForm': 'Formulaire de contact',
      'contact.quickActions': 'Actions rapides',
      'contact.trainingLocations': 'Lieux de formation',
      'contact.registrationLinks': 'Liens d\'inscription rapide',
      'contact.faq': 'Questions fréquentes',
      'contact.socialNetworks': 'Réseaux sociaux',
      'contact.cta': 'Appel à l\'action'
    },
    'Paramètres globaux': {
      'global.company': 'Informations de l\'entreprise',
      'global.socialMedia': 'Liens des réseaux sociaux'
    },
    'Paramètres Admin': {
      'admin.settings': 'Informations d\'identification'
    }
  };

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await apiClient.verifyToken();
        if (response.valid && response.user) {
          setCurrentUser(response.user);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  const handleSave = async () => {
    const result = await saveContent();
    setSaveMessage(result.message);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const renderSidebarItem = (key, label, level = 0) => (
    <div
      key={key}
      className={`sidebar-item level-${level} ${selectedSection === key ? 'active' : ''}`}
      onClick={() => {
        setSelectedSection(key);
        closeMobileSidebar(); // Close mobile sidebar when section is selected
      }}
    >
      <span className="sidebar-label">{label}</span>
      {hasUnsavedChanges && selectedSection === key && (
        <span className="unsaved-indicator">●</span>
      )}
    </div>
  );

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <button 
            className="mobile-sidebar-toggle"
            onClick={toggleMobileSidebar}
            title="Menu de navigation"
          >
            <i className="fas fa-bars"></i>
          </button>
          <h1><i className="fas fa-cog"></i> DentoXcellence CMS</h1>
          <span className="admin-subtitle">Système de Gestion de Contenu</span>
        </div>
        <div className="admin-header-right">
          {hasUnsavedChanges && (
            <span className="unsaved-changes">
              <i className="fas fa-exclamation-circle"></i>
              Modifications non sauvegardées
            </span>
          )}
          <button 
            className={`save-btn ${isLoading ? 'loading' : ''}`}
            onClick={handleSave}
            disabled={isLoading || !hasUnsavedChanges}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Sauvegarde en cours...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Sauvegarder les modifications
              </>
            )}
          </button>
          <button 
            className="logout-btn"
            onClick={onLogout}
            title="Déconnexion"
          >
            <i className="fas fa-sign-out-alt"></i>
            Déconnexion
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
          <i className={`fas ${saveMessage.includes('Error') ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
          {saveMessage}
        </div>
      )}

      {/* Mobile Overlay */}
      <div 
        className={`mobile-sidebar-overlay ${isMobileSidebarOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      ></div>

      <div className="admin-content">
        {/* Sidebar */}
        <div className={`admin-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <h3><i className="fas fa-list"></i> Sections de contenu</h3>
          </div>
          <div className="sidebar-content">
            {Object.entries(contentStructure).map(([categoryName, categoryItems]) => (
              <div key={categoryName} className="sidebar-category">
                <div className="category-header">
                  <h4>{categoryName}</h4>
                </div>
                <div className="category-items">
                  {Object.entries(categoryItems).map(([key, label]) => 
                    renderSidebarItem(key, label, 1)
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Editor */}
        <div className="admin-main">
          <div className="editor-header">
            <h2>
              <i className="fas fa-edit"></i>
              Modification : {contentStructure[Object.keys(contentStructure).find(category => 
                Object.keys(contentStructure[category]).includes(selectedSection)
              )]?.[selectedSection] || 'Section inconnue'}
            </h2>
            <div className="editor-path">
              <i className="fas fa-map-marker-alt"></i>
              Chemin : {selectedSection}
            </div>
          </div>
          
          <div className="editor-content">
            {selectedSection === 'admin.settings' ? (
              <AdminSettings currentUser={currentUser} />
            ) : selectedSection === 'formations.formationsGrid' ? (
              <SimpleFormationsAdmin key={selectedSection} />
            ) : (
              <ContentEditor 
                contentPath={selectedSection}
                key={selectedSection} // Force re-render when section changes
              />
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;
